import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription, finalize, take, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../modules/auth';
import {
  validateemailval,
  validateretry,
} from '../../../modules/auth/models/auth.model';
import { AuthservicesService } from '../../services/authservices.service';
const helper = new JwtHelperService();

/** The three delivery channels a verification code can be sent over. */
type Channel = 'sms' | 'call' | 'email';

const SENT_MESSAGE: Record<Channel, string> = {
  sms: 'Please check your mobile device for the code.',
  call: 'We are calling you now with your code.',
  email: 'Please check your email for the code.',
};

const FAILED_MESSAGE: Record<Channel, string> = {
  sms: "We couldn't text your code.",
  call: "We couldn't call you with your code.",
  email: "We couldn't email your code.",
};
/**
 * The API passes Twilio's error text through in `message`. Translate the ones
 * a user can act on; anything else falls back to the generic message.
 */
export function friendlyVerifyError(message: unknown): string | null {
  const text = String(message ?? '').toLowerCase();
  if (text.includes('max send attempts')) {
    return 'Too many codes have been requested for this destination. Please wait about 10 minutes, or try a different method below.';
  }
  if (text.includes('max check attempts')) {
    return 'Too many incorrect codes were entered. Please request a new code.';
  }
  if (text.includes('not a valid phone number') || text.includes('invalid parameter `to`')) {
    return 'The mobile number on your account looks invalid. Please try email, or contact your administrator.';
  }
  if (text.includes('blocked') || text.includes('fraud')) {
    return 'Our messaging provider blocked this request. Please try email, or contact your administrator.';
  }
  return null;
}

/** sessionStorage key so a page reload doesn't send another code during a cooldown. */
const COOLDOWN_STORAGE_KEY = 'gd-mfa-cooldowns';

@Component({
  selector: 'app-authcode',
  imports: [CommonModule, RouterModule, FormsModule, ReactiveFormsModule],
  templateUrl: './factcheck.component.html',
  styleUrl: './factcheck.component.scss',
  standalone: true,
})
export class FactcheckComponent implements OnInit, OnDestroy {
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // How long the user must wait between sends, and how long we are willing to
  // wait for the server before calling it a failure. The request budget is
  // deliberately shorter than the cooldown so a hung request can never outlive
  // the window in which the user is blocked from retrying.
  private static readonly COOLDOWN_SECONDS = 40;
  private static readonly REQUEST_TIMEOUT_MS = 20000;

  // Verification method signal
  verificationMethod = signal<'sms' | 'call' | 'email'>('sms');

  // Each channel has its own cooldown, so after texting a code the user can
  // still switch to email or a call straight away; only the channel just used
  // is locked. Cooldowns are derived from wall-clock deadlines rather than
  // accumulated ticks, so they always resolve -- even if the tab is
  // backgrounded, change detection is skipped, or a tick is missed.
  readonly channels: Channel[] = ['sms', 'call', 'email'];
  cooldownLeft: Record<Channel, number> = { sms: 0, call: 0, email: 0 };
  private cooldownUntil: Record<Channel, number | null> = { sms: null, call: null, email: null };
  private cooldownTimer: any = null;

  /** Channel currently being sent, so only its button shows a spinner. */
  sendingChannel: Channel | null = null;

  /** Seconds left before the active channel can be resent (used by the template). */
  get countdownValue(): number {
    return this.cooldownLeft[this.verificationMethod()];
  }

  // In-flight guards. isSending gates the resend/switch buttons, isVerifying
  // gates the submit button. Both are released via finalize().
  isSending = false;
  isVerifying = false;
  sendError: string | null = null;

  private navTimers: any[] = [];
  private lockTimer: any = null;
  private viewReady = false;

  @ViewChild('seconds', { static: true }) seconds: ElementRef | undefined;
  subscription: Subscription | undefined;
  public valid: boolean | undefined;
  codeForm: FormGroup;
  getStorage: any | undefined;
  getphonenumber: string = '';
  subscriptions: Subscription[] = [];
  public isValid: boolean | undefined;
  public disableTextbox: boolean | undefined;
  public rememberDevice = {
    RememberUser: false,
  };
  maskedPhone: string = '';
  maskedEmail: string = '';
  hasMobileNumber: boolean = false;
  /** Raw destinations read once from the JWT, so we never re-decode per send. */
  private mobileNumber: string = '';
  private emailAddress: string = '';
  maxAttempts = 5;
  attempts = 0;
  remainingAttempts = 5;
  cooldownTime = 30;
  isLocked = false;
  lockSecondsLeft = 0;
  submitted = false;
  private downloadTimer: any = null;
  private originalPhone: string = '';
  codeFieldIndices = Array.from({ length: 6 }, (_, i) => i);
  
  // Mobile registration modal state
  showMobilePrompt = false;
  verificationJustSucceeded = false;
  mobileRegistrationForm: FormGroup;
  registeringMobile = false;
  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private typeservice: AuthservicesService,
    private auth: AuthService,
    private toastr: ToastrService
  ) {
    this.codeForm = this.fb.group({
      // Twilio Verify code length is set per service (4-10 digits), so don't assume one.
      token: ['', [Validators.required, Validators.pattern(/^\d{4,10}$/)]],
      rememberUser: [false],
    });
    
    this.mobileRegistrationForm = this.fb.group({
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]]
    });
    
    this.getStorage = localStorage.getItem(this.authLocalStorageToken);
    let decodedToken: any;
    if (!this.getStorage) {
      this.router.navigate(['/auth/']);
      return;
    }

    let decoded: any;
    try {
      const { token } = JSON.parse(this.getStorage) as { token?: string };
      if (!token) {
        this.router.navigate(['/auth/']);
        return;
      }
      decoded = helper.decodeToken(token);
    } catch (err) {
      console.error('Failed to parse token:', err);
      this.router.navigate(['/auth/']);
      return;
    }
    this.auth.currentUser$.pipe(take(1)).subscribe((user) => {
      if (!user) {
        this.router.navigate(['/auth/']);
        return;
      }

      const choice = (decoded.VerifyChoice || '').toUpperCase();
      this.mobileNumber = decoded.PhoneNumber || '';
      this.emailAddress =
        decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ||
        '';

      // Set mobile availability flag
      this.hasMobileNumber = !!this.mobileNumber;

      // Keep the masked email around for the "Try Email" option regardless of
      // which channel we end up using.
      if (this.emailAddress) {
        this.maskedEmail = this.maskEmail(this.emailAddress);
      }

      // VOICE/CALL -> call, EMAIL -> email, anything else (including null or an
      // unrecognised value) -> sms.
      let channel: Channel =
        choice === 'EMAIL' ? 'email' : choice === 'VOICE' || choice === 'CALL' ? 'call' : 'sms';

      // Fall back to email if the chosen channel needs a phone number we don't have.
      if ((channel === 'sms' || channel === 'call') && !this.mobileNumber) {
        channel = 'email';
      }

      if (channel === 'email' && !this.emailAddress) {
        // Nothing we can deliver a code to. Don't bounce silently -- explain it.
        this.sendError =
          'There is no mobile number or email address on your account to send a verification code to. Please contact your administrator.';
        return;
      }

      this.restoreCooldowns();
      if (this.cooldownLeft[channel] > 0) {
        // A code was sent moments ago (e.g. the page was reloaded). Don't spend
        // another Twilio send attempt; the code already sent is still valid.
        this.verificationMethod.set(channel);
        this.applyMask(channel);
        return;
      }
      this.sendCode(channel);
    });
    // Don't remove token here since we need it for verification
    // We'll remove it if verification fails
    // localStorage.removeItem(this.authLocalStorageToken);
  }

  ngOnInit() {
    this.viewReady = true;
  }

  /**
   * Nudge change detection. The initial send can run synchronously from the
   * constructor (currentUser$ is a BehaviorSubject), which is before the view
   * exists -- detectChanges() would throw there, so skip until ngOnInit.
   */
  private refresh(): void {
    if (this.viewReady) {
      this.cdr.detectChanges();
    }
  }

  // ---------------------------------------------------------------------------
  // Cooldown
  // ---------------------------------------------------------------------------

  /**
   * Start the resend cooldown for one channel. Only ever called after a send is
   * confirmed successful -- a failed send must leave the user free to retry
   * immediately.
   */
  private startCooldown(channel: Channel, seconds = FactcheckComponent.COOLDOWN_SECONDS): void {
    this.cooldownUntil[channel] = Date.now() + seconds * 1000;
    this.saveCooldowns();
    this.tickCooldown();
    if (!this.cooldownTimer) {
      this.cooldownTimer = setInterval(() => this.tickCooldown(), 1000);
    }
  }

  private clearCooldown(channel: Channel): void {
    this.cooldownUntil[channel] = null;
    this.saveCooldowns();
    this.tickCooldown();
  }

  private saveCooldowns(): void {
    try {
      sessionStorage.setItem(COOLDOWN_STORAGE_KEY, JSON.stringify(this.cooldownUntil));
    } catch {
      // Storage unavailable -- cooldowns just won't survive a reload.
    }
  }

  private restoreCooldowns(): void {
    try {
      const saved = JSON.parse(sessionStorage.getItem(COOLDOWN_STORAGE_KEY) || '{}');
      for (const ch of this.channels) {
        const until = Number(saved?.[ch]);
        if (until > Date.now()) {
          this.cooldownUntil[ch] = until;
        }
      }
    } catch {
      return;
    }
    if (this.channels.some((ch) => this.cooldownUntil[ch] !== null)) {
      this.tickCooldown();
      if (!this.cooldownTimer) {
        this.cooldownTimer = setInterval(() => this.tickCooldown(), 1000);
      }
    }
  }

  /**
   * Recompute the remaining seconds for every channel from its deadline.
   * Because this is derived from the clock rather than counted down, a missed
   * tick can only ever make a cooldown resolve sooner -- never leave the UI
   * gated forever. The interval stops once no channel is cooling down.
   */
  private tickCooldown(): void {
    const now = Date.now();
    let anyActive = false;
    for (const ch of this.channels) {
      const until = this.cooldownUntil[ch];
      const remaining = until === null ? 0 : Math.max(0, Math.ceil((until - now) / 1000));
      this.cooldownLeft[ch] = remaining;
      if (remaining === 0) {
        this.cooldownUntil[ch] = null;
      } else {
        anyActive = true;
      }
    }
    if (!anyActive && this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
    this.refresh();
  }

  /** Whether a channel's button can be used right now. */
  canSend(channel: Channel): boolean {
    return !this.isSending && this.cooldownLeft[channel] === 0;
  }

  /** Whether the account has a destination for a channel. */
  hasChannel(channel: Channel): boolean {
    return channel === 'email' ? !!this.emailAddress : !!this.mobileNumber;
  }

  // ---------------------------------------------------------------------------
  // Sending a code -- one path for text, email and voice
  // ---------------------------------------------------------------------------

  /**
   * Single entry point for every code send. Guarantees that all three channels
   * behave identically: one request in flight, the spinner always released, a
   * `success:false` body treated as a failure (the API returns HTTP 200 even
   * when delivery fails), and the cooldown started only on a confirmed send.
   */
  private sendCode(channel: Channel): void {
    if (!this.canSend(channel)) {
      return;
    }

    if ((channel === 'sms' || channel === 'call') && !this.mobileNumber) {
      this.failSend(channel, 'There is no mobile number on file for your account.');
      return;
    }
    if (channel === 'email' && !this.emailAddress) {
      this.failSend(channel, 'There is no email address on file for your account.');
      return;
    }

    this.isSending = true;
    this.sendingChannel = channel;
    this.sendError = null;

    this.requestFor(channel)
      .pipe(
        take(1),
        timeout(FactcheckComponent.REQUEST_TIMEOUT_MS),
        finalize(() => {
          this.isSending = false;
          this.sendingChannel = null;
          this.refresh();
        })
      )
      .subscribe({
        next: (response: any) => {
          if (response && response.success) {
            this.verificationMethod.set(channel);
            this.applyMask(channel);
            this.isValid = true;
            this.valid = false;
            this.startCooldown(channel);
            this.toastr.success(SENT_MESSAGE[channel], 'Code sent');
            this.focusCodeInput();
          } else {
            // HTTP 200 with success:false -- delivery failed server-side.
            this.failSend(channel, friendlyVerifyError(response?.message) ?? FAILED_MESSAGE[channel]);
          }
        },
        error: (err: unknown) => {
          console.error(`Failed to send ${channel} verification code`, err);
          this.failSend(channel, FAILED_MESSAGE[channel]);
        },
      });
  }

  /** Map a channel to its service call. */
  private requestFor(channel: Channel): Observable<any> {
    switch (channel) {
      case 'call':
        return this.typeservice.RetryverifyToken({
          RememberUser: this.codeForm.value.rememberUser,
        } as validateretry);
      case 'email':
        return this.typeservice.getUserByEmail({
          email: this.emailAddress,
          RememberUser: this.codeForm.value.rememberUser,
        } as validateemailval);
      case 'sms':
      default:
        return this.typeservice.registercurrentprofile();
    }
  }

  /**
   * Surface a send failure and make sure the user is not left waiting: no
   * cooldown is started for that channel (or a running one is cleared), so
   * they can retry or switch channel straight away.
   */
  private failSend(channel: Channel, message: string): void {
    this.clearCooldown(channel);
    this.sendError = message.includes('Please')
      ? message
      : `${message} Please try again, or use one of the other options below.`;
    this.toastr.error(this.sendError, "Couldn't send code");
  }

  private applyMask(channel: Channel): void {
    if (channel === 'email') {
      this.maskedEmail = this.maskEmail(this.emailAddress);
    } else if (this.mobileNumber) {
      this.maskedPhone = this.maskPhonenumber(this.mobileNumber);
    }
  }

  private maskPhonenumber(phone: string): string {
    if (!phone) {
      return '';
    }
    return phone.slice(0, -4).replace(/\d/g, '*') + phone.slice(-4);
  }
  private maskEmail(email: string): string {
    const [local, domain] = email.split('@');

    // If there's no '@', just mask last 4 of the whole string
    if (!domain) {
      if (local.length <= 4) {
        return '*'.repeat(local.length);
      }
      return local.slice(0, local.length - 4) + '*'.repeat(4);
    }

    // If local-part is too short to carve out 5 + 4, just mask everything after the first char
    if (local.length <= 9) {
      const first = local[0] ?? '';
      const restMasked = '*'.repeat(local.length - 1);
      return `${first}${restMasked}@${domain}`;
    }

    const start = local.slice(0, 5);
    const end = local.slice(-4);
    const middleMask = '*'.repeat(local.length - 5 - 4);

    return `${start}${middleMask}${end}@${domain}`;
  }

  // Channel buttons. All three funnel into the one sendCode() path so text,
  // email and voice share identical loading, success, error and cooldown handling.
  onResend() {
    this.sendCode('sms');
  }
  onSMScall() {
    this.sendCode('sms');
  }
  onResendcall() {
    this.sendCode('call');
  }
  onEmailcall() {
    this.sendCode('email');
  }
  onEmailVerification() {
    this.sendCode('email');
  }

  /** Resend over whichever channel is currently active. */
  onResendCode() {
    this.sendCode(this.verificationMethod());
  }

  /** Send (or resend) over a specific channel from the method picker. */
  onSelectChannel(channel: Channel) {
    this.sendCode(channel);
  }

  /**
   * Keep only digits (so a pasted "123 4567" works). No auto-submit: the code
   * length is configured in Twilio, so the user presses Continue / Enter.
   */
  onCodeInput(): void {
    const control = this.codeForm.get('token');
    const raw = String(control?.value ?? '');
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (digits !== raw) {
      control?.setValue(digits, { emitEvent: false });
    }
  }

  private focusCodeInput(): void {
    setTimeout(() => (document.getElementById('code_one') as HTMLInputElement | null)?.focus());
  }

  focusNextInput(index: number) {
    const nextInput = document.getElementById(`code_one`) as HTMLInputElement;
    if (nextInput) {
      nextInput.focus();
    }
  }

  onSubmit(): void {
    // Already verifying -- ignore the extra click rather than firing a second
    // VerifyToken request, which would burn an attempt against the provider.
    if (this.isLocked || this.isVerifying) return;

    // Only handle code verification here
    if (this.codeForm.invalid) {
      this.toastr.error('Please enter a valid verification code', 'Error');
      return;
    }

    if (this.attempts < this.maxAttempts) {
      // Store token before verification
      localStorage.setItem(this.authLocalStorageToken, this.getStorage);

      const params = {
        Token: this.codeForm.value.token,
        RememberUser: this.codeForm.value.rememberUser,
      };

      this.isVerifying = true;

      this.typeservice
        .verifyToken(params)
        .pipe(
          take(1),
          timeout(FactcheckComponent.REQUEST_TIMEOUT_MS),
          finalize(() => {
            this.isVerifying = false;
            this.refresh();
          })
        )
        .subscribe({
          next: (response: any) => {
            if (response && response.success) {
              this.isValid = true;
              this.valid = false;
              this.toastr.success('Verification successful!', 'Success!');
              // Store the token properly without double-stringify
              localStorage.setItem(this.authLocalStorageToken, this.getStorage);
              this.attempts = 0;
              
              // Show mobile registration prompt if verified via email and no mobile number
              if (this.verificationMethod() === 'email' && !this.hasMobileNumber) {
                this.verificationJustSucceeded = true;
                this.showMobilePrompt = true;
                // Don't navigate immediately - let user respond to prompt
              } else {
                this.navigateAfter('/pages/dashboard', 1000);
              }
            } else {
              this.valid = true;
              this.isValid = false;
              this.disableTextbox = false;
              const reason = friendlyVerifyError(response?.message);
              if (reason) {
                this.sendError = reason;
              }
              this.toastr.error(reason ?? 'Incorrect code.', 'Error!');
              this.registerFailedAttempt();
            }
          },
          error: (err: HttpErrorResponse) => {
            this.valid = true;
            this.isValid = false;
            this.disableTextbox = false;
            console.error('Verification request failed', err);
            this.toastr.error(
              'Something went wrong. Try again later.',
              'Error!'
            );
            this.registerFailedAttempt();
          },
        });
    }
  }

  onLogout(): void {
    // Clear local storage and navigate to auth
    localStorage.removeItem(this.authLocalStorageToken);
    this.router.navigate(['/auth/']);
  }

  private registerFailedAttempt() {
    this.attempts++;
    // NOTE: the auth token is deliberately NOT cleared here. It is the same
    // token every subsequent request needs, and dropping it on a mistyped digit
    // used to break the retry the user was about to make.
    this.remainingAttempts = Math.max(0, this.maxAttempts - this.attempts);
    if (this.attempts >= this.maxAttempts) {
      this.lockForm();
    }
  }
  SetupStorage() {}

  /** Navigate after a short delay, tracking the timer so it can be cancelled. */
  private navigateAfter(url: string, delayMs: number): void {
    this.navTimers.push(
      setTimeout(() => this.router.navigateByUrl(url), delayMs)
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
    this.navTimers.forEach((t) => clearTimeout(t));
    this.navTimers = [];
    if (this.lockTimer) {
      clearTimeout(this.lockTimer);
      this.lockTimer = null;
    }
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  lockForm(): void {
    this.isLocked = true;
    this.lockSecondsLeft = this.cooldownTime;
    // Tick the visible counter so the lockout is never a silent no-op.
    const ticker = setInterval(() => {
      this.lockSecondsLeft = Math.max(0, this.lockSecondsLeft - 1);
      this.refresh();
    }, 1000);
    this.lockTimer = setTimeout(() => {
      clearInterval(ticker);
      this.attempts = 0;
      this.remainingAttempts = this.maxAttempts;
      this.lockSecondsLeft = 0;
      this.isLocked = false;
      this.lockTimer = null;
      this.refresh();
    }, this.cooldownTime * 1000);
  }

  // Mobile Registration Methods
  onCloseMobilePrompt(): void {
    this.showMobilePrompt = false;
    // Navigate to dashboard after closing prompt
    this.navigateAfter('/pages/dashboard', 500);
  }

  onAddMobileNumber(): void {
    if (this.mobileRegistrationForm.invalid) {
      this.toastr.error('Please enter a valid 10-digit mobile number', 'Invalid Number');
      return;
    }

    this.registeringMobile = true;
    const phoneNumber = this.mobileRegistrationForm.value.phoneNumber;
    const params = { phoneNumber:'+1' + phoneNumber };
    this.typeservice.registermobilenew(params).subscribe({
      next: (response: any) => {
        this.registeringMobile = false;
        if (response && response.success) {
          this.toastr.success(
            'Mobile number added successfully! You can now use text messages for verification.',
            'Success!'
          );
          this.hasMobileNumber = true;
          this.maskedPhone = this.maskPhonenumber(phoneNumber);
          this.showMobilePrompt = false;
          
          // Navigate to dashboard after successful registration
          this.navigateAfter('/pages/dashboard', 1500);
        } else {
          this.toastr.error(
            'Failed to add mobile number. Please try again later.',
            'Registration Failed'
          );
        }
      },
      error: (err: HttpErrorResponse) => {
        this.registeringMobile = false;
        this.toastr.error(
          'An error occurred while adding your mobile number. Please try again.',
          'Error'
        );
        console.error('Mobile registration error:', err);
      }
    });
  }
}
